import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";

import { MenuItem, SelectChangeEvent } from "@mui/material";
import { Grid } from "@mui/material";
import { parseISO } from "date-fns";
import { format, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Input, Select } from "../../../../../../../../../../components/Form";
import { getDateValueInTimeZone } from "../../../../../../../../../../components/Generic/MilestoneDates";
import { AppSwitch } from "../../../../../../../../../../components/Generic/AppSwitch";
import Modal from "../../../../../../../../../../components/Generic/Modal";
import { useAppState } from "../../../../../../../../../../context/StateContext";
import useYupValidation from "../../../../../../../../../../hooks/useYupValidation";
import {
  FleetAvailability,
  FleetAvailabilityAllDayAbsence,
  FleetAvailabilityPartialAbsence,
} from "../../../../../../../../../../models/fleet.availability";
import { FleetAvailabilityType } from "../../../../../../../../../../util/enums/fleet";
import { useEquipmentContext } from "../../../context/Equipment";
import useStyles, { AlldayLabel, AlldaySection, ContentBox, ModalSection } from "./styles";

export type ManageAvailabilityHandles = {
  open: (root?: FleetAvailability) => void;
  close: () => void;
};

type Props = {
  isLoading: boolean;
  onAddNewAvailability?: (
    data: FleetAvailabilityAllDayAbsence | FleetAvailabilityPartialAbsence
  ) => void;
};

const ManageAvailabilityComponent: ForwardRefRenderFunction<ManageAvailabilityHandles, Props> = (
  { onAddNewAvailability, isLoading },
  ref
) => {
  const classes = useStyles();

  const { currentUser, userTimezone } = useAppState();
  const { equipment } = useEquipmentContext();

  const { t: translate } = useTranslation();

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { getErrorByField } = useYupValidation(errors);

  function isEndDateAfterThenStartDate(startDateString?: string, endDateString?: string) {
    if (startDateString && endDateString) {
      const startDate = new Date(startDateString);
      const endDate = new Date(endDateString);

      return endDate >= startDate;
    }

    return false;
  }

  const schema = yup.object().shape({
    type: yup
      .string()
      .typeError(translate("thisFieldIsRequired"))
      .required(translate("thisFieldIsRequired")),
    allDayAbsence: yup.boolean(),
    from: yup.string().when("allDayAbsence", {
      is: true,
      then: yup
        .string()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired"))
        .test("isFromDateLaterThenToDate", translate("pleaseSelectAnEarlierDate"), (startDate) =>
          isEndDateAfterThenStartDate(startDate, endDate)
        ),
      otherwise: yup.string().nullable(),
    }),
    to: yup.string().when("allDayAbsence", {
      is: true,
      then: yup
        .string()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired"))
        .test("isToDateLaterThenFromDate", translate("pleaseSelectAnLaterDate"), (endDate) =>
          isEndDateAfterThenStartDate(startDate, endDate)
        ),
      otherwise: yup.string().nullable(),
    }),
    date: yup.string().when("allDayAbsence", {
      is: false,
      then: yup
        .string()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
    startTime: yup.string().when("allDayAbsence", {
      is: false,
      then: yup
        .string()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
    endTime: yup.string().when("allDayAbsence", {
      is: false,
      then: yup
        .string()
        .typeError(translate("thisFieldIsRequired"))
        .required(translate("thisFieldIsRequired")),
      otherwise: yup.string().nullable(),
    }),
  });

  const [isOpened, setIsOpened] = useState(false);
  const [, setRootData] = useState<FleetAvailability | null>(null);

  const [type, setType] = useState("");
  const [isAllDay, setIsAllDay] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const toggleIsAllDay = (value: boolean) => {
    setIsAllDay(value);
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
  };

  const onResetFields = useCallback(() => {
    setType("");
    setIsAllDay(false);
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
  }, []);

  const onValidate = () => {
    try {
      schema.validateSync(
        {
          type,
          allDayAbsence: isAllDay,
          date: startDate,
          from: startDate,
          to: endDate,
          startTime,
          endTime,
        },
        { abortEarly: false }
      );

      setErrors([]);

      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setErrors(err.inner);
      }

      return false;
    }
  };

  const onSubmitForm = () => {
    if (onValidate()) {
      let data: FleetAvailabilityAllDayAbsence | FleetAvailabilityPartialAbsence;
      const dayStartTime = "00:00";
      const dayEndTime = "00:00";

      if (isAllDay) {
        data = {
          type,
          allDayAbsence: isAllDay,
          from: joinDateTimeUTC(startDate, dayStartTime),
          to: joinDateTimeUTC(endDate, dayEndTime),
          equipmentId: Number(equipment?.id),
          companyId: currentUser?.companyId,
        } as FleetAvailabilityAllDayAbsence;
      } else {
        data = {
          type,
          allDayAbsence: isAllDay,
          from: joinDateTimeUTC(startDate, startTime),
          to: joinDateTimeUTC(startDate, endTime),
          equipmentId: Number(equipment?.id),
          companyId: currentUser?.companyId,
        } as FleetAvailabilityPartialAbsence;
      }

      if (data) onAddNewAvailability?.(data);
    }
  };

  const getDateValue = (date: string) => {
    if (!date) return null;

    return format(
      utcToZonedTime(
        date,
        userTimezone?.name || Intl.DateTimeFormat().resolvedOptions().timeZone
      ),
      "yyyy-MM-dd"
    );
  };

  const setDateValue = (newDate: string, action: React.Dispatch<React.SetStateAction<string>>) => {
    const currentTimezone = userTimezone?.name || Intl.DateTimeFormat().resolvedOptions().timeZone;

    const dateToAct = zonedTimeToUtc(newDate, currentTimezone);

    if (dateToAct) {
      action(dateToAct.toISOString());
    }
  };

  const joinDateTimeUTC = (date: string, time: string) => {
    const currentTimezone = userTimezone?.name;
    const dateUTC = parseISO(date);
    const dateZonedTime = utcToZonedTime(dateUTC.toISOString(), currentTimezone);

    const [hours, minutes] = time.split(":").map(Number);

    dateZonedTime.setHours(hours, minutes, 0, 0);

    const dateTimeUTC = zonedTimeToUtc(dateZonedTime, currentTimezone);

    return dateTimeUTC.toISOString().replace("T", " ").replace(":00.000Z", "");
  };

  const renderSectionContent = () => {
    if (isAllDay) {
      return (
        <Grid item xs={12} container direction="row" spacing={4}>
          <Grid item xs={6}>
            <Input
              name="startDate"
              required
              label={translate("from")}
              type="date"
              value={startDate ? getDateValue(startDate) : ""}
              onChange={(event) => setDateValue(event.target.value, setStartDate)}
              error={!!getErrorByField("from")}
              errorMessage={getErrorByField("from")}
            />
          </Grid>
          <Grid item xs={6}>
            <Input
              name="endDate"
              required
              label={translate("to")}
              type="date"
              value={endDate ? getDateValue(endDate) : ""}
              onChange={(event) => setDateValue(event.target.value, setEndDate)}
              error={!!getErrorByField("to")}
              errorMessage={getErrorByField("to")}
            />
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid item xs={12} container direction="row" spacing={4}>
        <Grid item xs={12}>
          <Input
            name="startDate"
            required
            label={translate("date")}
            type="date"
            value={startDate ? getDateValue(startDate) : ""}
            onChange={(event) => setDateValue(event.target.value, setStartDate)}
            error={!!getErrorByField("date")}
            errorMessage={getErrorByField("date")}
          />
        </Grid>

        <Grid item xs={6}>
          <Input
            name="starTime"
            required
            label={translate("startTime")}
            type="time"
            value={startTime}
            onChange={(event) => {
              setStartTime(event.target.value);
            }}
            error={!!getErrorByField("startTime")}
            errorMessage={getErrorByField("startTime")}
          />
        </Grid>
        <Grid item xs={6}>
          <Input
            name="endTime"
            required
            label={translate("endTime")}
            type="time"
            value={endTime}
            onChange={(event) => {
              setEndTime(event.target.value);
            }}
            error={!!getErrorByField("endTime")}
            errorMessage={getErrorByField("endTime")}
          />
        </Grid>
      </Grid>
    );
  };

  const onOpenModal = useCallback(
    (root?: FleetAvailability) => {
      setRootData(root || null);
      if (root) {
        setIsAllDay(root?.allDayAbsence);
        setType(root?.type);
        setStartDate(root?.dateFrom || "");
        setEndDate(root?.dateTo || "");
        setStartTime(getDateValueInTimeZone(root?.dateFrom, "HH:mm", userTimezone?.name) || "");
        setEndTime(getDateValueInTimeZone(root?.dateTo, "HH:mm", userTimezone?.name) || "");
      }

      setIsOpened(true);
    },
    [userTimezone?.name]
  );

  const onCloseModal = useCallback(() => {
    onResetFields();
    setRootData(null);
    setIsOpened(false);
    setErrors([]);
  }, [onResetFields]);

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  return (
    <Modal
      open={isOpened}
      handleClose={onCloseModal}
      title={translate("newUnavailabilityTime")}
      data-cy="absenceModal"
      dataCyTitle="absenceModalTitle"
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: onCloseModal,
        cancelDisabled: isLoading,
        dataCySave: "submitChangesButton",
        textSave: translate("create"),
        onSave: onSubmitForm,
        loading: isLoading,
      }}
      showStatusField={false}
      className={classes.modal}
    >
      <ModalSection>
        <Grid container direction="row" alignItems="flex-start">
          <Grid item xs={12}>
            <Select
              label={translate("type")}
              required
              onChange={(event: SelectChangeEvent<any>) => setType(event.target.value)}
              value={type}
              placeholder={translate("selectOne")}
              error={!!getErrorByField("type")}
              errorMessage={getErrorByField("type")}
            >
              {Object.values(FleetAvailabilityType).map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <ContentBox item xs={12} container direction="column">
            <AlldaySection
              item
              xs={12}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
              marginBottom={"8px"}
            >
              <AlldayLabel>{translate("allDayUnavailable")}</AlldayLabel>
              <AppSwitch value={isAllDay} onChange={() => toggleIsAllDay(!isAllDay)} />
            </AlldaySection>
            {renderSectionContent()}
          </ContentBox>
        </Grid>
      </ModalSection>
    </Modal>
  );
};

const ManageAvailability = forwardRef(ManageAvailabilityComponent);

export default ManageAvailability;
